import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import * as XLSX from 'xlsx';
import axios from 'axios';
import '../shared.css';

const PAGE_SIZE_OPTIONS = [50, 100, 200];

// ── In-memory cache so navigating away and back is instant ──────
let _cachedCompanyId = null;
let _cachedData      = [];

// ── Memoised row — only re-renders if its own data changes ──────
const ItemRow = memo(({ item, index, hasEdit, hasDelete, onDelete }) => (
  <tr key={item.Id}>
    <td>{index}</td>
    <td>{item.Code}</td>
    <td>{item.Name}</td>
    <td style={{ textAlign: 'right' }}>{item.MRP}</td>
    <td style={{ textAlign: 'right' }}>{item.DiscountPercentage}</td>
    <td style={{ textAlign: 'right' }}>{item.GSTRate}</td>
    <td>{item.HSNCode}</td>
    <td className="actions-cell">
      {hasEdit && (
        <Link to={`/itemEdit/${item.Id}`} style={styles.actionLink} title="Edit">✏️</Link>
      )}
      {hasDelete && (
        <button style={styles.actionBtn} title="Delete" onClick={() => onDelete(item.Id)}>🗑️</button>
      )}
    </td>
  </tr>
));

function ItemIndex() {
  const { isAuthenticated, companyId, branchId, userId, permissions, roleName, token, logout } = useAuth();
  const history = useHistory();

  const [data, setData]                         = useState(_cachedData);
  const [searchQuery, setSearchQuery]           = useState('');
  const [debouncedQuery, setDebouncedQuery]     = useState('');
  const [showSearch, setShowSearch]             = useState(false);
  const [deleteItemId, setDeleteItemId]         = useState(null);
  const [confirmOpen, setConfirmOpen]           = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [fileData, setFileData]                 = useState([]);
  const [isImporting, setIsImporting]           = useState(false);
  const [isFetching, setIsFetching]             = useState(false);

  // Pagination
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const hasCreate = permissions.includes('Create Master');
  const hasEdit   = permissions.includes('Edit Master');
  const hasDelete = permissions.includes('Delete Master');

  // ── Debounce search (300 ms) ────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQuery(searchQuery); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ── Fetch — skip if cache is still valid for this company ───
  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    if (_cachedCompanyId === companyId && _cachedData.length > 0) {
      setData(_cachedData); // instant — no network call
      return;
    }
    fetchTableData();
  }, [isAuthenticated, companyId, branchId, roleName, token, logout]);

  const fetchTableData = useCallback((bustCache = false) => {
    setIsFetching(true);
    fetchData('ItemApi', `/${companyId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          _cachedCompanyId = companyId;
          _cachedData      = response.response_data;
          setData(response.response_data);
        }
      })
      .catch((error) => {
        console.error('Error fetching Item data:', error);
        if (error.response?.status === 401) {
          logout();
          history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
        }
      })
      .finally(() => setIsFetching(false));
  }, [companyId, token, logout, history]);

  const handleDelete = useCallback(() => {
    deleteData('ItemApi', `${deleteItemId}`, token)
      .then(() => {
        // Remove from cache + state without re-fetching
        _cachedData = _cachedData.filter(d => d.Id !== deleteItemId);
        setData(_cachedData);
        setDeleteItemId(null);
        setConfirmOpen(false);
      })
      .catch(err => console.error('Error deleting record:', err));
  }, [deleteItemId, token]);

  const openDelete = useCallback((id) => {
    setDeleteItemId(id);
    setConfirmOpen(true);
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const raw = new Uint8Array(e.target.result);
      const workbook = XLSX.read(raw, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      setFileData(
        sheetData.slice(1)
          .filter(row => row[2])
          .map(row => ({
            Material:    row[0]  ? String(row[0])  : '',
            Description: row[1]  ? String(row[1])  : '',
            PG:          row[2]  ? String(row[2])  : '',
            IG:          row[3]  ? String(row[3])  : '',
            ISG:         String(row[4]),
            PF:          row[5]  ? String(row[5])  : '',
            Group:       row[6]  ? String(row[6])  : '',
            HSN:         row[8]  ? String(row[8])  : '',
            MIKCode:     row[9]  ? String(row[9])  : '',
            GST:         row[12] ?? 0,
            MRP:         row[7]  ?? 0,
            Category:    row[11] ?? 0,
          }))
      );
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      await axios.post('https://cr-api.off-ease.com/api/ItemImportApi',
        { CompanyId: companyId, BranchId: branchId, CreatedBy: userId, ItemImport: fileData },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      // Bust cache after import so fresh data loads
      _cachedCompanyId = null;
      _cachedData      = [];
      setImportDialogOpen(false);
      fetchTableData(true);
    } catch (error) {
      console.error('Error importing data:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // ── Filter (memoised — only recomputes when data or query changes) ──
  const filtered = useMemo(() => {
    if (!debouncedQuery) return data;
    const q = debouncedQuery.toLowerCase();
    return data.filter(item =>
      item.Code.toLowerCase().includes(q) ||
      item.Name.toLowerCase().includes(q)
    );
  }, [data, debouncedQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageStart  = (safePage - 1) * pageSize;
  const pageRows   = useMemo(
    () => filtered.slice(pageStart, pageStart + pageSize),
    [filtered, pageStart, pageSize]
  );

  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

  return (
    <div className="pg-container">
      {/* ── Header ── */}
      <div className="pg-header">
        <h5 className="pg-title">Item</h5>
        <div className="pg-header-actions">
          <div className="pg-action-row">
            <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
              🔍 Search
            </button>
            {showSearch && (
              <input
                className="pg-input pg-search-input"
                placeholder="Search by code or name…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
                style={{ width: 200 }}
              />
            )}
            {hasCreate && roleName === 'SuperAdmin' && (
              <Link to="/ItemCreate" className="pg-btn pg-btn-primary">
                + Create New
              </Link>
            )}
            {hasCreate && (
              <button className="pg-btn pg-btn-gold" onClick={() => setImportDialogOpen(true)}>
                ⬆ Import Item
              </button>
            )}
            <button className="pg-btn pg-btn-outline" title="Refresh"
              onClick={() => { _cachedCompanyId = null; _cachedData = []; fetchTableData(true); }}>
              ↻ Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="pg-table-wrap">
        {isFetching
          ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <CircularProgress size={32} />
            </div>
          ) : (
            <table className="pg-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Code</th>
                  <th>Name</th>
                  <th style={{ textAlign: 'right' }}>MRP</th>
                  <th style={{ textAlign: 'right' }}>Disc %</th>
                  <th style={{ textAlign: 'right' }}>GST</th>
                  <th>HSN</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0
                  ? <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                  : pageRows.map((item, i) => (
                    <ItemRow
                      key={item.Id}
                      item={item}
                      index={pageStart + i + 1}
                      hasEdit={hasEdit}
                      hasDelete={hasDelete}
                      onDelete={openDelete}
                    />
                  ))
                }
              </tbody>
            </table>
          )
        }
      </div>

      {/* ── Pagination bar ── */}
      {!isFetching && filtered.length > 0 && (
        <div style={styles.paginationBar}>
          <span style={styles.paginationInfo}>
            {filtered.length.toLocaleString()} records &nbsp;|&nbsp;
            Showing {pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)}
          </span>

          <div style={styles.paginationControls}>
            <button style={styles.pgNavBtn} onClick={() => goTo(1)} disabled={safePage === 1}>«</button>
            <button style={styles.pgNavBtn} onClick={() => goTo(safePage - 1)} disabled={safePage === 1}>‹</button>
            {buildPageNumbers(safePage, totalPages).map((p, idx) =>
              p === '…'
                ? <span key={`e${idx}`} style={styles.pgEllipsis}>…</span>
                : <button key={p} style={{ ...styles.pgPageBtn, ...(p === safePage ? styles.pgPageBtnActive : {}) }}
                    onClick={() => goTo(p)}>{p}</button>
            )}
            <button style={styles.pgNavBtn} onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages}>›</button>
            <button style={styles.pgNavBtn} onClick={() => goTo(totalPages)} disabled={safePage === totalPages}>»</button>
          </div>

          <div style={styles.pageSizeWrap}>
            <span style={styles.paginationInfo}>Rows:</span>
            <select style={styles.pageSizeSelect} value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Record?</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this item?</DialogContentText></DialogContent>
        <DialogActions>
          <button className="pg-btn pg-btn-danger" onClick={() => setConfirmOpen(false)}>Cancel</button>
          <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
        </DialogActions>
      </Dialog>

      {/* ── Import Dialog ── */}
      <Dialog open={importDialogOpen} onClose={() => { setImportDialogOpen(false); setFileData([]); }}>
        <DialogTitle>Import Items</DialogTitle>
        <DialogContent>
          <DialogContentText>Upload an Excel file to import items. Ensure the file format is correct.</DialogContentText>
          <input type="file" onChange={handleFileChange} style={{ marginTop: 12 }} />
        </DialogContent>
        <DialogActions>
          <button className="pg-btn pg-btn-danger" onClick={() => { setImportDialogOpen(false); setFileData([]); }}>Cancel</button>
          <button className="pg-btn pg-btn-success" onClick={handleImport} disabled={isImporting}>
            {isImporting ? <CircularProgress size={16} color="inherit" /> : 'Import'}
          </button>
        </DialogActions>
      </Dialog>

      <div className="pg-watermark">©<b>EFFTABS SOLUTIONS LLP</b></div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────
function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 4) pages.push('…');
  for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) pages.push(i);
  if (current < total - 3) pages.push('…');
  pages.push(total);
  return pages;
}

const styles = {
  actionLink: { padding: '2px 6px', fontSize: 15, textDecoration: 'none', cursor: 'pointer' },
  actionBtn:  { padding: '2px 6px', fontSize: 15, background: 'none', border: 'none', cursor: 'pointer' },
  paginationBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 8, marginTop: 8, padding: '6px 4px',
  },
  paginationInfo:    { fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' },
  paginationControls:{ display: 'flex', alignItems: 'center', gap: 3 },
  pgNavBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, border: '1.5px solid rgba(200,212,224,0.7)',
    borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#1c2a3a',
  },
  pgPageBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 28, height: 28, border: '1.5px solid rgba(200,212,224,0.7)',
    borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: 12,
    fontWeight: 600, color: '#1c2a3a', padding: '0 6px',
  },
  pgPageBtnActive: {
    background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)',
    color: '#fff', borderColor: '#1c2a3a',
  },
  pgEllipsis:   { fontSize: 13, color: '#94a3b8', padding: '0 4px' },
  pageSizeWrap: { display: 'flex', alignItems: 'center', gap: 6 },
  pageSizeSelect: {
    height: 28, padding: '0 6px', border: '1.5px solid rgba(200,212,224,0.7)',
    borderRadius: 5, fontSize: 12, fontWeight: 600, color: '#1c2a3a',
    background: '#fff', cursor: 'pointer',
  },
};

export default ItemIndex;
